import { API } from 'aws-amplify';
import { Auth } from 'aws-amplify';
import { gql } from '@apollo/client';
import apolloClient from '../graphql/apollo-client';
import type { GraphQLResult } from '@aws-amplify/api-graphql';

// GraphQL endpoint and API key for unauthenticated calls (direct fetch).
// Both MUST be supplied by build configuration — there is deliberately no
// hardcoded fallback, so a misconfigured build fails loudly instead of
// silently talking to another environment with a credential baked into
// source. See SECURITY.md.
function requireGraphqlEndpoint(): string {
  const endpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;
  if (!endpoint) {
    throw new Error('REACT_APP_GRAPHQL_ENDPOINT is not configured');
  }
  return endpoint;
}

function requireGraphqlApiKey(): string {
  const apiKey = process.env.REACT_APP_GRAPHQL_API_KEY;
  if (!apiKey) {
    throw new Error('REACT_APP_GRAPHQL_API_KEY is not configured');
  }
  return apiKey;
}
import {
  ApiResponse,
  User,
  Influencer,
  Campaign,
  CampaignRegistrationFormData,
  Match,
  SearchFilters
} from '../types';

// Recursively strip __typename from objects before sending to GraphQL mutations
function stripTypename(obj: any): any {
  if (Array.isArray(obj)) return obj.map(stripTypename);
  if (obj && typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (key === '__typename') continue;
      cleaned[key] = stripTypename(value);
    }
    return cleaned;
  }
  return obj;
}
import { UserType } from '../constants/userTypes';

// GraphQL imports
import {
  GET_USER,
  GET_HOST_PROFILE,
  GET_HOST_PROFILE_BY_USER_ID,
  GET_HOST_PROFILES,
  GET_INFLUENCERS,
  GET_INFLUENCER,
  GET_CAMPAIGNS,
  GET_CAMPAIGNS_BY_HOST,
  GET_CAMPAIGN,
  GET_MATCHES,
  GET_MATCH,
  GET_UPLOAD_URL
} from '../graphql/queries';

import { GET_INFLUENCER_BY_USER_ID, GET_INFLUENCERS_BY_IDS } from '../graphql/queries';
import {
  SIGN_UP,
  SIGN_IN,
  CONFIRM_EMAIL,
  COMPLETE_OAUTH_REGISTRATION,
  UPDATE_USER,
  CREATE_HOST_PROFILE,
  UPDATE_HOST_PROFILE,
  CREATE_INFLUENCER,
  UPDATE_INFLUENCER,
  CREATE_CAMPAIGN,
  UPDATE_CAMPAIGN,
  CREATE_MATCH,
  UPDATE_MATCH,
  START_IDENTITY_VERIFICATION,
  COMPLETE_IDENTITY_VERIFICATION,
  SEND_EMAIL_VERIFICATION,
  CONFIRM_EMAIL_VERIFICATION,
  WITHDRAW_ACCOUNT,
  LINK_SOCIAL_ACCOUNT,
  UNLINK_SOCIAL_ACCOUNT,
  CONNECT_YOUTUBE_CHANNEL,
  CONNECT_INSTAGRAM_ACCOUNT
} from '../graphql/mutations';

import {
  ON_MATCH_STATUS_CHANGED,
  ON_CAMPAIGN_CREATED,
  ON_MATCH_CREATED,
  ON_USER_UPDATED
} from '../graphql/subscriptions';

const client = API;  // AWS Amplify API
const apolloGqlClient = apolloClient;  // Apollo Client for testing

// Helper function to check authentication before making requests
const ensureAuthenticated = async () => {
  try {
    const session = await Auth.currentSession();
    if (!session.getAccessToken()) {
      console.warn('⚠️ No access token in session');
      return false;
    }
    return true;
  } catch (error) {
    // Check if we have a token in the auth store (Naver OAuth users)
    const { useAuthStore } = await import('../store/authStore');
    const { token } = useAuthStore.getState();
    if (token) {
      console.log('ℹ️ Using stored token for authentication');
      return true;
    }
    console.warn('⚠️ No authentication available');
    return false;
  }
};

// Helper function to handle GraphQL responses
const handleGraphQLResponse = <T>(response: GraphQLResult<any>): ApiResponse<T> => {
  if (response.errors && response.errors.length > 0) {
    return {
      success: false,
      error: response.errors[0].message,
    };
  }

  return {
    success: true,
    data: response.data,
  };
};

export const graphqlApi = {
  // Auth operations using GraphQL mutations for DynamoDB integration
  async signUp(email: string, password: string, name: string, userType: UserType): Promise<ApiResponse> {
    try {
      // Use GraphQL mutation for signup to create both Cognito user and DynamoDB record
      const response = await client.graphql({
        query: SIGN_UP,
        variables: {
          input: {
            email,
            password,
            name,
            userType,
          },
        },
      }) as GraphQLResult<{ signUp: any }>;

      const result = response.data?.signUp;
      if (result?.success) {
        return {
          success: true,
          message: result.message,
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result?.error || 'Registration failed',
        };
      }
    } catch (error: any) {
      console.error('SignUp error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed',
      };
    }
  },

  async signIn(email: string, password: string): Promise<ApiResponse> {
    try {
      // Use GraphQL mutation for sign in
      const response = await client.graphql({
        query: SIGN_IN,
        variables: {
          input: {
            email,
            password,
          },
        },
      }) as GraphQLResult<{ signIn: any }>;

      const result = response.data?.signIn;
      if (result?.success) {
        return {
          success: true,
          message: result.message || 'Login successful!',
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result?.error || 'Login failed',
        };
      }
    } catch (error: any) {
      console.error('SignIn error:', error);
      return {
        success: false,
        error: error.message || 'Login failed',
      };
    }
  },

  async confirmEmail(email: string, confirmationCode: string): Promise<ApiResponse> {
    try {
      // Use GraphQL mutation for email confirmation
      const response = await client.graphql({
        query: CONFIRM_EMAIL,
        variables: {
          input: {
            email,
            confirmationCode,
          },
        },
      }) as GraphQLResult<{ confirmEmail: any }>;

      const result = response.data?.confirmEmail;
      if (result?.success) {
        return {
          success: true,
          message: result.message,
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result?.error || 'Email confirmation failed',
        };
      }
    } catch (error: any) {
      console.error('ConfirmEmail error:', error);
      return {
        success: false,
        error: error.message || 'Email confirmation failed',
      };
    }
  },

  async completeOAuthRegistration(input: {
    userId: string;
    email: string;
    name: string;
    userType: string;
    profileImage?: string | null;
    oauthProvider?: string;
    tosAcceptedVersion?: string;
    tosAcceptedAt?: string;
    marketingConsent?: boolean;
    naverId?: string;
    kakaoId?: string;
    facebookId?: string;
  }): Promise<ApiResponse> {
    try {
      // Prefer the Cognito ID token if the user already has a session. For
      // NEW Naver users the token is held back by handleNaverCallback until
      // registration succeeds, so Auth.currentSession() throws here —
      // we fall back to the AppSync API key which the mutation also
      // accepts (schema: `@aws_api_key @aws_cognito_user_pools`).
      let headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      try {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        if (token) headers['Authorization'] = token;
      } catch {
        headers['x-api-key'] = requireGraphqlApiKey();
      }

      const response = await fetch(requireGraphqlEndpoint(), {
        method: 'POST',
        headers,
        body: JSON.stringify({
          query: COMPLETE_OAUTH_REGISTRATION,
          variables: { input },
        }),
      });

      const data = await response.json();
      const result = data?.data?.completeOAuthRegistration;

      if (result?.success) {
        return { success: true, message: result.message, data: result.data };
      }
      const errorMsg = result?.error || data?.errors?.[0]?.message || 'Failed to complete registration';
      // Synthesize a stable errorCode for duplicate-email so the UI can
      // localize it without needing a schema change. The backend uses the
      // exact string `"An account with this email already exists."` in that
      // case.
      const errorCode =
        /account with this email already exists/i.test(errorMsg) ||
        /EmailAlreadyInUse/i.test(errorMsg)
          ? 'EmailAlreadyInUse'
          : undefined;
      return { success: false, error: errorMsg, ...(errorCode && { errorCode }) };
    } catch (error: any) {
      console.error('CompleteOAuthRegistration error:', error);
      return { success: false, error: error.message || 'Failed to complete registration' };
    }
  },

  // Alternative direct Amplify Auth methods (backup)
  async signUpDirect(email: string, password: string, name: string, userType: UserType): Promise<ApiResponse> {
    try {
      const result = await Auth.signUp({
        username: email,
        password,
        attributes: {
          email,
          name,
          'custom:userType': userType,
        },
      });

      return {
        success: true,
        message: 'Registration successful! Please check your email for verification code.',
        data: {
          userId: result.userSub,
          result: result,
        },
      };
    } catch (error: any) {
      console.error('SignUp error:', error);
      return {
        success: false,
        error: error.message || 'Registration failed',
      };
    }
  },

  async signInDirect(email: string, password: string): Promise<ApiResponse> {
    try {
      const result = await Auth.signIn({
        username: email,
        password,
      });

      if (result) {
        // Get tokens from the session
        const session = await Auth.currentSession();

        return {
          success: true,
          message: 'Login successful!',
          data: {
            accessToken: session.getAccessToken().getJwtToken(),
            idToken: session.getIdToken().getJwtToken(),
            isSignedIn: !!result,
          },
        };
      } else {
        return {
          success: false,
          error: 'Sign in not completed',
          data: { result },
        };
      }
    } catch (error: any) {
      console.error('SignIn error:', error);
      return {
        success: false,
        error: error.message || 'Login failed',
      };
    }
  },

  async confirmEmailDirect(email: string, confirmationCode: string): Promise<ApiResponse> {
    try {
      const result = await Auth.confirmSignUp(email, confirmationCode);

      return {
        success: true,
        message: 'Email confirmed successfully! You can now sign in.',
        data: {
          result: result,
        },
      };
    } catch (error: any) {
      console.error('ConfirmSignUp error:', error);
      return {
        success: false,
        error: error.message || 'Email confirmation failed',
      };
    }
  },

  // User operations

  /**
   * Check if a user record exists in DynamoDB (uses API key, no Cognito session needed).
   * Used by OAuthCallbackHandler to detect new users.
   */
  async checkUserExists(userId: string): Promise<{ exists: boolean; user?: User }> {
    try {
      const response = await fetch(requireGraphqlEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': requireGraphqlApiKey(),
        },
        body: JSON.stringify({
          query: GET_USER,
          variables: { userId },
        }),
      });

      const data = await response.json();
      console.log('🔍 checkUserExists response:', JSON.stringify(data));

      // If there are GraphQL errors (e.g. unauthorized), the user might still exist
      // but we can't verify — don't assume new user
      if (data?.errors?.length > 0) {
        console.log('⚠️ checkUserExists got GraphQL errors:', data.errors[0]?.message);
        // If it's an auth error, we can't determine — assume new user
        return { exists: false };
      }

      const user = data?.data?.getUser;
      return { exists: !!user, user: user || undefined };
    } catch (error) {
      console.error('❌ checkUserExists error:', error);
      // On error, assume existing user to avoid redirect loop
      return { exists: true };
    }
  },

  async getUser(userId: string): Promise<ApiResponse<User>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Getting user with Apollo Client...', { userId });

      const response = await apolloGqlClient.query({
        query: gql`${GET_USER}`,
        variables: { userId },
        fetchPolicy: 'network-only',
      }) as GraphQLResult<{ getUser: User }>;

      return handleGraphQLResponse<User>(response);
    } catch (error: any) {
      console.error('❌ GetUser error:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch user',
      };
    }
  },

  async updateUser(userData: Partial<User>): Promise<ApiResponse<User>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Updating user with Apollo Client...', userData);

      const response = await apolloGqlClient.mutate({
        mutation: gql`${UPDATE_USER}`,
        variables: {
          input: userData,
        },
      }) as GraphQLResult<{ updateUser: User }>;

      return handleGraphQLResponse<User>(response);
    } catch (error: any) {
      console.error('❌ UpdateUser error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update user',
      };
    }
  },

  async withdrawAccount(
    userId: string,
    reasons: string[],
    feedback?: string,
  ): Promise<{ success: boolean; message?: string; error?: string; blocked?: boolean; ongoingCount?: number }> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`${WITHDRAW_ACCOUNT}`,
        variables: { input: { userId, reasons, feedback } },
      }) as GraphQLResult<{ withdrawAccount: { success: boolean; message?: string; error?: string; blocked?: boolean; ongoingCount?: number } }>;
      return response.data?.withdrawAccount || { success: false, error: 'No response' };
    } catch (error: any) {
      console.error('❌ withdrawAccount error:', error);
      return { success: false, error: error.message || 'Failed to withdraw account' };
    }
  },

  async linkSocialAccount(
    userId: string,
    provider: string,
    code: string,
    redirectUri: string,
  ): Promise<{ success: boolean; message?: string; error?: string; provider?: string; alreadyLinkedToOther?: boolean }> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`${LINK_SOCIAL_ACCOUNT}`,
        variables: { input: { userId, provider, code, redirectUri } },
      }) as GraphQLResult<{ linkSocialAccount: any }>;
      return response.data?.linkSocialAccount || { success: false, error: 'No response' };
    } catch (error: any) {
      console.error('❌ linkSocialAccount error:', error);
      return { success: false, error: error.message || 'Failed to link social account' };
    }
  },

  async unlinkSocialAccount(
    userId: string,
    provider: string,
  ): Promise<{ success: boolean; message?: string; error?: string; provider?: string }> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`${UNLINK_SOCIAL_ACCOUNT}`,
        variables: { input: { userId, provider } },
      }) as GraphQLResult<{ unlinkSocialAccount: any }>;
      return response.data?.unlinkSocialAccount || { success: false, error: 'No response' };
    } catch (error: any) {
      console.error('❌ unlinkSocialAccount error:', error);
      return { success: false, error: error.message || 'Failed to unlink social account' };
    }
  },

  // Host Profile operations
  async getHostProfile(hostId: string): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Getting host profile with Apollo Client...', { hostId });

      const response = await apolloGqlClient.query({
        query: gql`${GET_HOST_PROFILE}`,
        variables: { hostId },
      }) as GraphQLResult<{ getHostProfile: any }>;

      return {
        success: true,
        data: response.data?.getHostProfile,
      };
    } catch (error: any) {
      console.error('❌ GetHostProfile error:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch host profile',
      };
    }
  },

  async getHostProfileByUserId(userId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apolloGqlClient.query({
        query: gql`${GET_HOST_PROFILE_BY_USER_ID}`,
        variables: { userId },
        fetchPolicy: 'network-only',
      });

      // The GraphQL response now returns a structured response
      const result = response.data?.getHostProfileByUserId;

      return {
        success: result?.success || false,
        data: result?.data,
        message: result?.message,
        error: result?.error,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch host profile',
      };
    }
  },

  async getHostProfiles(limit?: number): Promise<ApiResponse<{ hostProfiles: any[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Getting host profiles with Apollo Client...', { limit });

      const response = await apolloGqlClient.query({
        query: gql`${GET_HOST_PROFILES}`,
        variables: { limit },
      }) as GraphQLResult<{ getHostProfiles: { hostProfiles: any[]; count: number; total: number } }>;

      return {
        success: true,
        data: response.data?.getHostProfiles,
      };
    } catch (error: any) {
      console.error('❌ GetHostProfiles error:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch host profiles',
      };
    }
  },

  async createHostProfile(hostProfileData: any): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Creating host profile with Apollo Client...', hostProfileData);

      const response = await apolloGqlClient.mutate({
        mutation: gql`${CREATE_HOST_PROFILE}`,
        variables: {
          input: hostProfileData,
        },
      }) as GraphQLResult<{ createHostProfile: any }>;

      const result = response.data?.createHostProfile;
      if (result?.success) {
        return {
          success: true,
          message: result.message,
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result?.error || 'Failed to create host profile',
        };
      }
    } catch (error: any) {
      console.error('❌ CreateHostProfile error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create host profile',
      };
    }
  },

  async updateHostProfile(hostId: string, hostProfileData: any): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Updating host profile with Apollo Client...', { hostId, hostProfileData });

      const response = await apolloGqlClient.mutate({
        mutation: gql`${UPDATE_HOST_PROFILE}`,
        variables: {
          input: {
            hostId,
            ...hostProfileData,
          },
        },
      }) as GraphQLResult<{ updateHostProfile: any }>;

      const result = response.data?.updateHostProfile;
      if (result?.success) {
        return {
          success: true,
          message: result.message,
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result?.error || 'Failed to update host profile',
        };
      }
    } catch (error: any) {
      console.error('❌ UpdateHostProfile error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update host profile',
      };
    }
  },

  // Influencer operations
  async getInfluencers(filters?: SearchFilters): Promise<ApiResponse<{ influencers: Influencer[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`${GET_INFLUENCERS}`,
        variables: { filters },
      });

      return {
        success: true,
        data: response.data?.getInfluencers || { influencers: [], count: 0, total: 0 },
      };
    } catch (error: any) {
      console.error('Error fetching influencers:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch influencers',
      };
    }
  },

  async getInfluencer(influencerId: string): Promise<ApiResponse<Influencer>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`${GET_INFLUENCER}`,
        variables: { influencerId },
        errorPolicy: 'all', // Allow partial data and errors
      });

      // Handle case where influencer is not found (null result)
      if (!response.data?.getInfluencer) {
        console.log('⚠️ Influencer not found:', influencerId);
        return {
          success: false,
          error: 'Influencer not found',
          data: undefined,
        };
      }

      return {
        success: true,
        data: response.data?.getInfluencer,
      };
    } catch (error: any) {
      console.log('⚠️ Error fetching influencer:', influencerId, error.message);
      return {
        success: false,
        error: error.message || 'Failed to fetch influencer',
      };
    }
  },

  async getInfluencersByIds(influencerIds: string[]): Promise<ApiResponse<Influencer[]>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.query({
        query: gql`${GET_INFLUENCERS_BY_IDS}`,
        variables: { influencerIds },
        fetchPolicy: 'network-only',
      });
      return { success: true, data: response.data?.getInfluencersByIds || [] };
    } catch (error: any) {
      console.error('Error fetching influencers by IDs:', error);
      return { success: false, error: error.message, data: [] };
    }
  },

  async getInfluencerByUserId(userId: string): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`${GET_INFLUENCER_BY_USER_ID}`,
        variables: { userId },
        fetchPolicy: 'network-only',
      });

      // The GraphQL response returns a structured response
      const result = response.data?.getInfluencerByUserId;

      return {
        success: result?.success || false,
        data: result?.data,
        message: result?.message,
        error: result?.error,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch influencer profile',
      };
    }
  },

  async connectYoutubeChannel(code: string, redirectUri: string): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.mutate({
        mutation: gql`${CONNECT_YOUTUBE_CHANNEL}`,
        variables: { code, redirectUri },
      });

      const result = (response.data as any)?.connectYoutubeChannel;

      return {
        success: result?.success || false,
        data: result,
        error: result?.error,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to connect YouTube channel',
      };
    }
  },

  async connectInstagramAccount(code: string, redirectUri: string): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.mutate({
        mutation: gql`${CONNECT_INSTAGRAM_ACCOUNT}`,
        variables: { code, redirectUri },
      });

      const result = (response.data as any)?.connectInstagramAccount;

      return {
        success: result?.success || false,
        data: result,
        error: result?.error,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to connect Instagram account',
      };
    }
  },

  async createInfluencer(influencerData: Partial<Influencer>): Promise<ApiResponse<Influencer>> {
    try {
      console.log('🔍 [FRONTEND] Starting createInfluencer request:', {
        influencerData,
        hasName: !!influencerData.name,
        hasEmail: !!influencerData.email,
        hasBio: !!influencerData.bio,
        hasMainCategory: !!influencerData.mainCategory,
        hasLocation: !!influencerData.location,
        hasRatePerPost: !!influencerData.ratePerPost,
        ratePerPostValue: influencerData.ratePerPost,
        dataKeys: Object.keys(influencerData)
      });

      await ensureAuthenticated();

      console.log('🔄 Creating influencer with Apollo Client...');

      const response = await apolloGqlClient.mutate({
        mutation: gql`${CREATE_INFLUENCER}`,
        variables: {
          input: influencerData,
        },
      }) as GraphQLResult<{ createInfluencer: any }>;

      console.log('🔍 [FRONTEND] GraphQL response received:', {
        hasResponse: !!response,
        hasData: !!response.data,
        response: response
      });

      const result = response.data?.createInfluencer;
      if (result?.success) {
        return {
          success: true,
          message: result.message,
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result?.error || 'Failed to create influencer',
        };
      }
    } catch (error: any) {
      console.error('🔍 [FRONTEND] GraphQL createInfluencer error details:', {
        errorType: typeof error,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
        hasErrors: !!error?.errors,
        errors: error?.errors,
        fullError: error
      });

      // Log each individual error for clarity
      if (error?.errors && Array.isArray(error.errors)) {
        error.errors.forEach((err: any, index: number) => {
          console.error(`🔍 [FRONTEND] GraphQL Error ${index + 1}:`, {
            message: err.message,
            locations: err.locations,
            path: err.path,
            extensions: err.extensions,
            fullError: err
          });
        });
      }

      return {
        success: false,
        error: error.message || 'Failed to create influencer',
      };
    }
  },

  async updateInfluencer(influencerId: string, influencerData: Partial<Influencer>): Promise<ApiResponse<Influencer>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Updating influencer with Apollo Client...', { influencerId, influencerData });

      const response = await apolloGqlClient.mutate({
        mutation: gql`${UPDATE_INFLUENCER}`,
        variables: {
          input: {
            influencerId,
            ...influencerData,
          },
        },
      }) as GraphQLResult<{ updateInfluencer: any }>;

      const result = response.data?.updateInfluencer;
      if (result?.success) {
        return {
          success: true,
          message: result.message,
          data: result.data,
        };
      } else {
        return {
          success: false,
          error: result?.error || 'Failed to update influencer',
        };
      }
    } catch (error: any) {
      console.error('❌ UpdateInfluencer error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create influencer',
      };
    }
  },

  // Phone verification operations


  // 휴대폰 본인확인 (NICE identity verification)
  async startIdentityVerification(): Promise<ApiResponse<{
    requestId: string;
    popupUrl: string;
    formFields: Record<string, string>;
  }>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.mutate({
        mutation: gql`${START_IDENTITY_VERIFICATION}`,
      }) as GraphQLResult<{
        startIdentityVerification: {
          success: boolean;
          requestId?: string;
          popupUrl?: string;
          formFields?: string;
          message?: string;
          error?: string;
        };
      }>;

      const result = response.data?.startIdentityVerification;
      if (!result?.success || !result.requestId || !result.popupUrl) {
        return {
          success: false,
          // The backend reports an unprovisioned NICE account with this
          // sentinel so the UI can explain rather than showing a raw error.
          error: result?.error === 'IDENTITY_VERIFICATION_UNAVAILABLE'
            ? result.message || '본인인증 서비스가 아직 준비되지 않았습니다.'
            : result?.error || 'Failed to start identity verification',
        };
      }

      let formFields: Record<string, string> = {};
      if (result.formFields) {
        try {
          formFields = JSON.parse(result.formFields);
        } catch {
          return { success: false, error: 'Malformed verification request' };
        }
      }

      return {
        success: true,
        data: { requestId: result.requestId, popupUrl: result.popupUrl, formFields },
      };
    } catch (error: any) {
      console.error('❌ StartIdentityVerification error:', error);
      return { success: false, error: error.message || 'Failed to start identity verification' };
    }
  },

  async completeIdentityVerification(requestId: string): Promise<ApiResponse<{
    identityVerified: boolean;
    identityName?: string;
    phoneNumber?: string;
  }>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.mutate({
        mutation: gql`${COMPLETE_IDENTITY_VERIFICATION}`,
        variables: { input: { requestId } },
      }) as GraphQLResult<{
        completeIdentityVerification: {
          success: boolean;
          identityVerified?: boolean;
          identityName?: string;
          phoneNumber?: string;
          message?: string;
          error?: string;
        };
      }>;

      const result = response.data?.completeIdentityVerification;
      if (!result?.success) {
        return { success: false, error: result?.error || 'Identity verification failed' };
      }
      return {
        success: true,
        message: result.message,
        data: {
          identityVerified: !!result.identityVerified,
          identityName: result.identityName,
          phoneNumber: result.phoneNumber,
        },
      };
    } catch (error: any) {
      console.error('❌ CompleteIdentityVerification error:', error);
      return { success: false, error: error.message || 'Identity verification failed' };
    }
  },

  // Email verification — used by the OAuth complete-registration flow for
  // Naver users who need to supply a real email. Unlike the phone flow,
  // these calls run BEFORE the user is fully registered, so they use the
  // AppSync API key rather than the user's Cognito session.
  async sendEmailVerification(email: string): Promise<ApiResponse<{ success: boolean; message?: string; error?: string }>> {
    try {
      const response = await fetch(requireGraphqlEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': requireGraphqlApiKey(),
        },
        body: JSON.stringify({
          query: SEND_EMAIL_VERIFICATION,
          variables: { input: { email } },
        }),
      });
      const json = await response.json();
      const result = json?.data?.sendEmailVerification;
      if (result?.success) {
        return { success: true, message: result.message, data: result };
      }
      return {
        success: false,
        error: result?.error || json?.errors?.[0]?.message || 'Failed to send verification email',
      };
    } catch (error: any) {
      console.error('❌ SendEmailVerification error:', error);
      return { success: false, error: error.message || 'Failed to send verification email' };
    }
  },

  async confirmEmailVerification(email: string, verificationCode: string): Promise<ApiResponse<{ success: boolean; message?: string; error?: string }>> {
    try {
      const response = await fetch(requireGraphqlEndpoint(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': requireGraphqlApiKey(),
        },
        body: JSON.stringify({
          query: CONFIRM_EMAIL_VERIFICATION,
          variables: { input: { email, verificationCode } },
        }),
      });
      const json = await response.json();
      const result = json?.data?.confirmEmailVerification;
      if (result?.success) {
        return { success: true, message: result.message, data: result };
      }
      return {
        success: false,
        error: result?.error || json?.errors?.[0]?.message || 'Invalid verification code',
      };
    } catch (error: any) {
      console.error('❌ ConfirmEmailVerification error:', error);
      return { success: false, error: error.message || 'Invalid verification code' };
    }
  },

  // Withdrawal operations
  async getWithdrawals(userId: string): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();
      const { GET_WITHDRAWALS } = await import('../graphql/queries');
      const response = await apolloGqlClient.query({
        query: gql`${GET_WITHDRAWALS}`,
        variables: { userId },
        fetchPolicy: 'network-only',
      }) as any;
      const result = response.data?.getWithdrawals;
      return { success: true, data: result };
    } catch (error: any) {
      console.error('❌ GetWithdrawals error:', error);
      return { success: false, error: error.message || 'Failed to fetch withdrawals' };
    }
  },

  async requestWithdrawal(userId: string, amount: number): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();
      const { REQUEST_WITHDRAWAL } = await import('../graphql/mutations');
      const response = await apolloGqlClient.mutate({
        mutation: gql`${REQUEST_WITHDRAWAL}`,
        variables: { input: { userId, amount } },
      }) as any;
      const result = response.data?.requestWithdrawal;
      if (result?.success) {
        return { success: true, data: result.data, message: result.message };
      }
      return { success: false, error: result?.error || 'Failed to request withdrawal' };
    } catch (error: any) {
      console.error('❌ RequestWithdrawal error:', error);
      return { success: false, error: error.message || 'Failed to request withdrawal' };
    }
  },

  async createInfluencerReview(input: any): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();
      const { CREATE_INFLUENCER_REVIEW } = await import('../graphql/mutations');
      const response = await apolloGqlClient.mutate({
        mutation: gql`${CREATE_INFLUENCER_REVIEW}`,
        variables: { input },
      }) as any;
      const result = response.data?.createInfluencerReview;
      if (result?.success) {
        return { success: true, data: result.data };
      }
      return { success: false, error: result?.error || 'Failed to create review' };
    } catch (error: any) {
      console.error('❌ CreateInfluencerReview error:', error);
      return { success: false, error: error.message || 'Failed to create review' };
    }
  },

  // Campaign operations
  async getCampaigns(filters?: any, limit?: number, nextToken?: string): Promise<ApiResponse<{ campaigns: Campaign[]; count: number; total: number; nextToken?: string | null }>> {
    try {
      // Ensure user is authenticated and get session info
      console.log('🔍 Checking authentication before getCampaigns...', { filters, limit, hasNextToken: !!nextToken });

      const session = await Auth.currentSession();
      console.log('📊 Session for getCampaigns:', {
        hasAccessToken: !!session.getAccessToken(),
        hasIdToken: !!session.getIdToken(),
        accessToken: session.getAccessToken().getJwtToken().substring(0, 50) + '...',
        idToken: session.getIdToken().getJwtToken().substring(0, 50) + '...',
      });

      // Ensure authentication is valid
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`${GET_CAMPAIGNS}`,
        variables: { filters, limit, nextToken },
      });

      console.log('✅ getCampaigns GraphQL response:', response);

      return {
        success: true,
        data: response.data?.getCampaigns,
      };
    } catch (error: any) {
      console.error('❌ getCampaigns error details:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      return {
        success: false,
        error: error.message || 'Failed to fetch campaigns',
      };
    }
  },

  /**
   * Fetch EVERY campaign matching `filters` by following the nextToken cursor.
   *
   * A single `getCampaigns` call returns one DynamoDB Scan page — the backend
   * caps it at 100 and defaults to 50 — so callers that need the complete set
   * (building a campaignId → campaign map, admin listings) must page. Using
   * `getCampaigns` alone silently truncates and makes campaigns outside the
   * first page look missing (e.g. rendered as "(삭제된 캠페인)").
   */
  async getAllCampaigns(filters?: any): Promise<ApiResponse<Campaign[]>> {
    const all: Campaign[] = [];
    let nextToken: string | undefined;

    try {
      // Hard iteration cap so a misbehaving cursor can't spin forever.
      for (let page = 0; page < 100; page++) {
        const res = await this.getCampaigns(filters, 100, nextToken);
        if (!res.success || !res.data) {
          // Surface the failure only if we got nothing at all; a partial
          // result is still more useful than none.
          if (all.length === 0) {
            return { success: false, error: res.error || 'Failed to fetch campaigns', data: [] };
          }
          break;
        }
        all.push(...(res.data.campaigns || []));
        nextToken = res.data.nextToken || undefined;
        if (!nextToken) break;
      }
      return { success: true, data: all };
    } catch (error: any) {
      console.error('❌ getAllCampaigns error:', error);
      return { success: false, error: error.message || 'Failed to fetch campaigns', data: all };
    }
  },

  async getCampaignsByHost(hostId: string, filters?: any, limit?: number): Promise<ApiResponse<{ campaigns: Campaign[]; count: number; total: number }>> {
    try {
      console.log('🔍 Getting campaigns by host...', { hostId, filters, limit });

      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`${GET_CAMPAIGNS_BY_HOST}`,
        variables: { hostId, filters, limit },
        fetchPolicy: 'network-only',
      });

      console.log('✅ getCampaignsByHost GraphQL response:', response);

      return {
        success: true,
        data: response.data?.getCampaignsByHost,
      };
    } catch (error: any) {
      console.error('❌ getCampaignsByHost error details:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch campaigns by host',
      };
    }
  },

  async getCampaign(campaignId: string): Promise<ApiResponse<Campaign>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`${GET_CAMPAIGN}`,
        variables: { campaignId },
        fetchPolicy: 'network-only',
      });

     console.log(`✅ getCampaignnnnn ${JSON.stringify(response)}`);
      return {
        success: true,
        data: response.data?.getCampaign,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch campaign',
      };
    }
  },

  async createCampaign(campaignData: CampaignRegistrationFormData): Promise<ApiResponse<Campaign>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Creating campaign with Apollo Client...', campaignData);

      const response = await apolloGqlClient.mutate({
        mutation: gql`${CREATE_CAMPAIGN}`,
        variables: {
          input: stripTypename(campaignData),
        },
      }) as GraphQLResult<{ createCampaign: Campaign }>;

      return {
        success: true,
        data: response.data?.createCampaign,
      };
    } catch (error: any) {
      console.error('❌ CreateCampaign error:', error);
      return {
        success: false,
        error: error.message || 'Failed to create campaign',
      };
    }
  },

  async updateCampaign(campaignId: string, campaignData: Partial<CampaignRegistrationFormData>): Promise<ApiResponse<Campaign>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Updating campaign with Apollo Client...', { campaignId, campaignData });

      const response = await apolloGqlClient.mutate({
        mutation: gql`${UPDATE_CAMPAIGN}`,
        variables: {
          input: stripTypename({
            campaignId,
            ...campaignData,
          }),
        },
      }) as GraphQLResult<{ updateCampaign: Campaign }>;

      return {
        success: true,
        data: response.data?.updateCampaign,
      };
    } catch (error: any) {
      console.error('❌ UpdateCampaign error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update campaign',
      };
    }
  },

  // Match operations
  async getMatches(filters?: any): Promise<ApiResponse<{ matches: Match[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`
          query GetMatches($filters: MatchFiltersInput) {
            getMatches(filters: $filters) {
              matches {
                matchId
                campaignId
                influencerId
                userId
                status
                proposedRate
                coverLetter
                matchScore
                selectedMissions
                visitStatus
                visitInfo {
                  startDate
                  endDate
                  adults
                  children
                  infants
                }
                contentSubmissions {
                  seq
                  channel
                  item
                  contentLink
                  submittedAt
                  status
                }
                appliedAt
                requestedAt
                respondedAt
                rewardAmount
                createdAt
                updatedAt
              }
              count
              total
            }
          }
        `,
        variables: { filters },
        fetchPolicy: 'network-only',
      });

      return {
        success: true,
        data: response.data?.getMatches || { matches: [], count: 0, total: 0 },
      };
    } catch (error: any) {
      console.error('Error fetching matches:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch matches',
      };
    }
  },

  async getMatch(matchId: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`${GET_MATCH}`,
        variables: { matchId },
      });

      return {
        success: true,
        data: response.data?.getMatch,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to fetch match',
      };
    }
  },

  async createMatch(matchData: Partial<Match>): Promise<ApiResponse<Match>> {
    try {
      const response = await apolloGqlClient.mutate({
        mutation: gql`${CREATE_MATCH}`,
        variables: {
          input: matchData,
        },
      });

      return {
        success: true,
        data: response.data?.createMatch,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create match',
      };
    }
  },

  async updateMatch(matchId: string, matchData: Partial<Match>): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Updating match with Apollo Client...', { matchId, matchData });

      const response = await apolloGqlClient.mutate({
        mutation: gql`${UPDATE_MATCH}`,
        variables: {
          input: {
            matchId,
            ...matchData,
          },
        },
      }) as GraphQLResult<{ updateMatch: Match }>;

      return {
        success: true,
        data: response.data?.updateMatch,
      };
    } catch (error: any) {
      console.error('❌ UpdateMatch error:', error);
      return {
        success: false,
        error: error.message || 'Failed to update match',
      };
    }
  },

  // Subscription operations
  subscribeToMatchStatusChanges(matchId: string, callback: (match: Match) => void) {
    const subscription = client.graphql({
      query: ON_MATCH_STATUS_CHANGED,
      variables: { matchId },
    });

    // Handle subscription for newer AWS Amplify versions
    if ('subscribe' in subscription) {
      return (subscription as any).subscribe({
        next: ({ data }: { data: any }) => {
          if (data?.onMatchStatusChanged) {
            callback(data.onMatchStatusChanged);
          }
        },
        error: (error: any) => {
          console.error('Match status subscription error:', error);
        },
      });
    }

    // Fallback for promise-based approach
    return subscription.then((result: any) => {
      console.log('Subscription result:', result);
    }).catch((error: any) => {
      console.error('Match status subscription error:', error);
    });
  },

  subscribeToCampaignCreated(category?: string, callback?: (campaign: Campaign) => void) {
    const subscription = client.graphql({
      query: ON_CAMPAIGN_CREATED,
      variables: { category },
    });

    // Handle subscription for newer AWS Amplify versions
    if ('subscribe' in subscription) {
      return (subscription as any).subscribe({
        next: ({ data }: { data: any }) => {
          if (data?.onCampaignCreated && callback) {
            callback(data.onCampaignCreated);
          }
        },
        error: (error: any) => {
          console.error('Campaign created subscription error:', error);
        },
      });
    }

    // Fallback for promise-based approach
    return subscription.then((result: any) => {
      console.log('Subscription result:', result);
    }).catch((error: any) => {
      console.error('Campaign created subscription error:', error);
    });
  },

  subscribeToMatchCreated(campaignId: string, callback: (match: Match) => void) {
    const subscription = client.graphql({
      query: ON_MATCH_CREATED,
      variables: { campaignId },
    });

    // Handle subscription for newer AWS Amplify versions
    if ('subscribe' in subscription) {
      return (subscription as any).subscribe({
        next: ({ data }: { data: any }) => {
          if (data?.onMatchCreated) {
            callback(data.onMatchCreated);
          }
        },
        error: (error: any) => {
          console.error('Match created subscription error:', error);
        },
      });
    }

    // Fallback for promise-based approach
    return subscription.then((result: any) => {
      console.log('Subscription result:', result);
    }).catch((error: any) => {
      console.error('Match created subscription error:', error);
    });
  },

  subscribeToUserUpdated(userId: string, callback: (user: User) => void) {
    const subscription = client.graphql({
      query: ON_USER_UPDATED,
      variables: { userId },
    });

    // Handle subscription for newer AWS Amplify versions
    if ('subscribe' in subscription) {
      return (subscription as any).subscribe({
        next: ({ data }: { data: any }) => {
          if (data?.onUserUpdated) {
            callback(data.onUserUpdated);
          }
        },
        error: (error: any) => {
          console.error('User updated subscription error:', error);
        },
      });
    }

    // Fallback for promise-based approach
    return subscription.then((result: any) => {
      console.log('Subscription result:', result);
    }).catch((error: any) => {
      console.error('User updated subscription error:', error);
    });
  },

  // Match management functions
  async shortlistMatch(matchId: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation ShortlistMatch($matchId: ID!) {
            shortlistMatch(matchId: $matchId) {
              matchId status updatedAt
            }
          }
        `,
        variables: { matchId }
      });
      return { success: true, data: response.data?.shortlistMatch };
    } catch (error: any) {
      console.error('Error shortlisting match:', error);
      return { success: false, error: error.message || 'Failed to shortlist match' };
    }
  },

  async notSelectMatch(matchId: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation NotSelectMatch($matchId: ID!) {
            notSelectMatch(matchId: $matchId) {
              matchId status updatedAt
            }
          }
        `,
        variables: { matchId }
      });
      return { success: true, data: response.data?.notSelectMatch };
    } catch (error: any) {
      console.error('Error not-selecting match:', error);
      return { success: false, error: error.message || 'Failed to not-select match' };
    }
  },

  async requestMatch(matchId: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation RequestMatch($matchId: ID!) {
            requestMatch(matchId: $matchId) {
              matchId status updatedAt
            }
          }
        `,
        variables: { matchId }
      });
      return { success: true, data: response.data?.requestMatch };
    } catch (error: any) {
      console.error('Error requesting match:', error);
      return { success: false, error: error.message || 'Failed to request match' };
    }
  },

  async stopCampaign(campaignId: string): Promise<ApiResponse<{ success: boolean; campaignId: string; notSelectedCount: number; continuingCount: number; status?: string }>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation StopCampaign($campaignId: ID!) {
            stopCampaign(campaignId: $campaignId) {
              success campaignId notSelectedCount continuingCount status
            }
          }
        `,
        variables: { campaignId }
      });
      return { success: true, data: response.data?.stopCampaign };
    } catch (error: any) {
      console.error('Error stopping campaign:', error);
      return { success: false, error: error.message || 'Failed to stop campaign' };
    }
  },

  async acceptMatch(matchId: string, options?: { selectedMissions?: string[]; visitInfo?: { startDate: string; endDate: string; adults: number; children: number; infants: number }; acceptanceNotes?: string }): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation AcceptMatch($matchId: ID!, $selectedMissions: [String!], $visitInfo: VisitInfoInput, $acceptanceNotes: String) {
            acceptMatch(matchId: $matchId, selectedMissions: $selectedMissions, visitInfo: $visitInfo, acceptanceNotes: $acceptanceNotes) {
              matchId status respondedAt updatedAt selectedMissions acceptanceNotes
              visitInfo { startDate endDate adults children infants }
            }
          }
        `,
        variables: { matchId, ...options }
      });
      return { success: true, data: response.data?.acceptMatch };
    } catch (error: any) {
      console.error('Error accepting match:', error);
      return { success: false, error: error.message || 'Failed to accept match' };
    }
  },

  async rejectMatch(matchId: string, rejectionReason: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation RejectMatch($matchId: ID!, $rejectionReason: String!) {
            rejectMatch(matchId: $matchId, rejectionReason: $rejectionReason) {
              matchId status rejectionReason respondedAt updatedAt
            }
          }
        `,
        variables: { matchId, rejectionReason }
      });
      return { success: true, data: response.data?.rejectMatch };
    } catch (error: any) {
      console.error('Error rejecting match:', error);
      return { success: false, error: error.message || 'Failed to reject match' };
    }
  },

  async cancelMatch(matchId: string, cancellationReason?: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation CancelMatch($matchId: ID!, $cancellationReason: String) {
            cancelMatch(matchId: $matchId, cancellationReason: $cancellationReason) {
              matchId status updatedAt
            }
          }
        `,
        variables: { matchId, cancellationReason }
      });
      return { success: true, data: response.data?.cancelMatch };
    } catch (error: any) {
      console.error('Error canceling match:', error);
      return { success: false, error: error.message || 'Failed to cancel match' };
    }
  },

  async withdrawMatch(matchId: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation WithdrawMatch($matchId: ID!) {
            withdrawMatch(matchId: $matchId) {
              matchId status updatedAt
            }
          }
        `,
        variables: { matchId }
      });
      return { success: true, data: response.data?.withdrawMatch };
    } catch (error: any) {
      console.error('Error withdrawing match:', error);
      return { success: false, error: error.message || 'Failed to withdraw match' };
    }
  },

  async completeMatch(matchId: string): Promise<ApiResponse<Match>> {
    try {
      await ensureAuthenticated();
      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation CompleteMatch($matchId: ID!) {
            completeMatch(matchId: $matchId) {
              matchId status completedAt updatedAt
            }
          }
        `,
        variables: { matchId }
      });
      return { success: true, data: response.data?.completeMatch };
    } catch (error: any) {
      console.error('Error completing match:', error);
      return { success: false, error: error.message || 'Failed to complete match' };
    }
  },

  /** @deprecated Use shortlistMatch/requestMatch/acceptMatch instead */
  async approveMatch(matchId: string, hostFeedback?: string): Promise<ApiResponse<Match>> {
    return this.shortlistMatch(matchId);
  },

  async getHostApplications(hostId: string): Promise<ApiResponse<{ matches: Match[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`
          query GetHostApplications($hostId: ID!) {
            getHostApplications(hostId: $hostId) {
              matches {
                matchId
                campaignId
                influencerId
                userId
                status
                proposedRate
                coverLetter
                portfolioItems
                estimatedDeliveryDays
                whyGoodFit
                previousWorkExamples
                hostFeedback
                rejectionReason
                matchScore
                appliedAt
                respondedAt
                completedAt
                createdAt
                updatedAt
              }
              count
              total
            }
          }
        `,
        variables: { hostId }
      }) as GraphQLResult<{ getHostApplications: { matches: Match[]; count: number; total: number } }>;

      // Handle null response from backend
      if (!response.data?.getHostApplications) {
        return {
          success: true,
          data: { matches: [], count: 0, total: 0 }
        };
      }

      return {
        success: true,
        data: response.data?.getHostApplications,
      };
    } catch (error: any) {
      console.error('Error fetching host applications:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch host applications',
      };
    }
  },

  async getRecentApplications(hostId: string, limit: number = 10, daysBack: number = 30): Promise<ApiResponse<{ matches: Match[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.query({
        query: gql`
          query GetRecentApplications($hostId: ID!, $limit: Int, $daysBack: Int) {
            getRecentApplications(hostId: $hostId, limit: $limit, daysBack: $daysBack) {
              matches {
                matchId
                campaignId
                influencerId
                userId
                status
                proposedRate
                coverLetter
                matchScore
                appliedAt
                respondedAt
                createdAt
                updatedAt
              }
              count
              total
            }
          }
        `,
        variables: { hostId, limit, daysBack }
      });

      // Handle GraphQL schema errors
      if (response.errors && response.errors.length > 0) {
        console.log('⚠️ GraphQL schema error detected in getRecentApplications, returning empty result');
        return {
          success: true,
          data: { matches: [], count: 0, total: 0 }
        };
      }

      if (!response.data?.getRecentApplications) {
        return {
          success: true,
          data: { matches: [], count: 0, total: 0 }
        };
      }

      return {
        success: true,
        data: response.data?.getRecentApplications,
      };
    } catch (error: any) {
      console.error('Error fetching recent applications:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch recent applications',
      };
    }
  },

  // Create new conversation
  async createConversation(campaignId: string, matchId: string, hostId: string, influencerId: string): Promise<ApiResponse<any>> {
    try {
      console.log('🔄 Creating conversation for match:', matchId);

      await ensureAuthenticated();

      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation CreateConversation($campaignId: ID!, $matchId: ID!, $hostId: ID!, $influencerId: ID!) {
            createConversation(campaignId: $campaignId, matchId: $matchId, hostId: $hostId, influencerId: $influencerId) {
              conversationId
              title
              campaignId
              matchId
              participants
              lastMessageId
              lastMessageAt
              unreadCount {
                userId
                count
              }
              createdAt
              updatedAt
            }
          }
        `,
        variables: { campaignId, matchId, hostId, influencerId }
      });

      console.log('✅ Conversation created successfully:', response.data?.createConversation?.conversationId);

      return {
        success: true,
        data: response.data?.createConversation,
      };
    } catch (error: any) {
      console.error('Error creating conversation:', error);
      return {
        success: false,
        error: error.message || 'Failed to create conversation',
      };
    }
  },


  // Check if conversation exists for a match
  async getConversationByMatchId(matchId: string): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      console.log('🔍 Getting conversation by matchId:', { matchId });

      const response = await apolloGqlClient.query({
        query: gql`
          query GetConversationByMatchId($matchId: ID!) {
            getConversationByMatchId(matchId: $matchId) {
              conversationId
              campaignId
              matchId
              hostId
              influencerId
              participants
              createdAt
            }
          }
        `,
        variables: { matchId }
      });

      return {
        success: true,
        data: response.data?.getConversationByMatchId || null,
      };
    } catch (error: any) {
      console.error('Error getting conversation by matchId:', error);
      return {
        success: false,
        error: error.message || 'Failed to get conversation by matchId',
        data: null,
      };
    }
  },

  // Get conversations by host ID
  async getConversationsByHostId(hostId: string, filters?: { limit?: number }): Promise<ApiResponse<{ conversations: any[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      console.log('🏢 Getting conversations for host:', { hostId, limit: filters?.limit });

      const response = await apolloGqlClient.query({
        query: gql`
          query GetConversationsByHostId($hostId: ID!, $limit: Int) {
            getConversationsByHostId(hostId: $hostId, limit: $limit) {
              conversations {
                conversationId
                campaignId
                matchId
                hostId
                influencerId
                participants
                lastMessage
                lastMessageAt
                unreadCount {
                  userId
                  count
                }
                createdAt
                updatedAt
              }
            }
          }
        `,
        variables: { hostId, limit: filters?.limit || 20 }
      });

      return {
        success: true,
        data: { conversations: response.data?.getConversationsByHostId?.conversations || [], count: 0, total: 0 },
      };
    } catch (error: any) {
      console.error('Error fetching host conversations:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch host conversations',
        data: { conversations: [], count: 0, total: 0 },
      };
    }
  },

  // Get conversations by influencer ID
  async getConversationsByInfluencerId(influencerId: string, filters?: { limit?: number }): Promise<ApiResponse<{ conversations: any[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      console.log('🌟 Getting conversations for influencer:', { influencerId, limit: filters?.limit });

      const response = await apolloGqlClient.query({
        query: gql`
          query GetConversationsByInfluencerId($influencerId: ID!, $limit: Int) {
            getConversationsByInfluencerId(influencerId: $influencerId, limit: $limit) {
              conversations {
                conversationId
                campaignId
                matchId
                hostId
                influencerId
                participants
                lastMessage
                lastMessageAt
                unreadCount {
                  userId
                  count
                }
                createdAt
                updatedAt
              }
            }
          }
        `,
        variables: { influencerId, limit: filters?.limit || 20 }
      });

      return {
        success: true,
        data: { conversations: response.data?.getConversationsByInfluencerId?.conversations || [], count: 0, total: 0 },
      };
    } catch (error: any) {
      console.error('Error fetching influencer conversations:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch influencer conversations',
        data: { conversations: [], count: 0, total: 0 },
      };
    }
  },

  // Get conversations - smart router function that detects user type and calls appropriate function
  async getConversations(filters?: { limit?: number }): Promise<ApiResponse<{ conversations: any[]; count: number; total: number }>> {
    try {
      await ensureAuthenticated();

      // Get current user to determine user type
      const session = await Auth.currentSession();
      const userId = session.getIdToken().payload.sub;

      console.log('🔄 Routing conversations for user:', { userId, limit: filters?.limit });

      // First, check if user is a host
      const hostProfileResponse = await this.getHostProfileByUserId(userId);

      if (hostProfileResponse.success && hostProfileResponse.data) {
        // User is a host - call dedicated host function
        const hostId = hostProfileResponse.data.hostId;
        console.log('👤 User is host, routing to getConversationsByHostId:', hostId);
        return await this.getConversationsByHostId(hostId, filters);
      }

      // Check if user is an influencer
      const influencerProfileResponse = await this.getInfluencerByUserId(userId);

      if (influencerProfileResponse.success && influencerProfileResponse.data) {
        // User is an influencer - call dedicated influencer function
        const influencerId = influencerProfileResponse.data.influencerId;
        console.log('🌟 User is influencer, routing to getConversationsByInfluencerId:', influencerId);
        return await this.getConversationsByInfluencerId(influencerId, filters);
      }

      // User has no profile yet - return empty conversations
      console.log('⚠️ User has no host or influencer profile, returning empty conversations');
      return {
        success: true,
        data: { conversations: [], count: 0, total: 0 },
      };

    } catch (error: any) {
      console.error('Error fetching conversations:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch conversations',
      };
    }
  },

  // Send message
  async sendMessage(messageInput: any): Promise<ApiResponse<any>> {
    try {
      await ensureAuthenticated();

      const response = await apolloGqlClient.mutate({
        mutation: gql`
          mutation SendMessage($input: SendMessageInput!) {
            sendMessage(input: $input) {
              messageId
              conversationId
              senderId
              receiverId
              messageType
              content
              attachments
              readStatus {
                isRead
                readAt
              }
              createdAt
              updatedAt
            }
          }
        `,
        variables: { input: messageInput }
      });

      return {
        success: true,
        data: response.data?.sendMessage,
      };
    } catch (error: any) {
      console.error('Error sending message:', error);
      return {
        success: false,
        error: error.message || 'Failed to send message',
      };
    }
  },

  // Get upload URL for S3 image upload
  async getUploadUrl(fileName: string, fileType: string, fileSize: number, allowAnyType?: boolean): Promise<ApiResponse<{ uploadUrl: string; fileUrl: string; fileKey: string }>> {
    try {
      await ensureAuthenticated();

      console.log('🔄 Getting upload URL:', { fileName, fileType, fileSize, allowAnyType });

      const response = await apolloGqlClient.query({
        query: gql`${GET_UPLOAD_URL}`,
        variables: { fileName, fileType, fileSize, allowAnyType }
      });

      return {
        success: true,
        data: response.data?.getUploadUrl,
      };
    } catch (error: any) {
      console.error('Error getting upload URL:', error);
      return {
        success: false,
        error: error.message || 'Failed to get upload URL',
      };
    }
  },
};
